/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import {
  FaChevronRight,
  FaDiscord,
  FaEye,
  FaEyeSlash,
  FaTrashAlt,
  FaUnlock,
} from 'react-icons/fa';
import { InputSimple } from '../form/input.Simple';
import { ModalGlobal } from '../modal/modal.Global';
import { BtnSimple } from '../form/btn.Simple';
import { BtnRound } from '../form/btn.Round';
import { setOpenModales } from '../../store/global';
import { canAccessItem } from '../../utils/item';
import { canUseMjForItem, isMjForItem } from '../../utils/auth';
import { hasResolvedPermissionScope } from '../../utils/permissions';
import {
  getStorageJSON,
  normalizePasswordEntries,
  setStorageJSON,
} from '../../utils/userStorage';
import { loadUserRoles, syncAuthSession } from '../../utils/accountProfile';
import {
  getMe,
  login,
  getDiscordAuthUrl,
  register,
  requestPasswordReset,
} from '../../services/auth';

const getLegacyPasswordValue = () =>
  window.localStorage.getItem('password') || '';
const getMjAccessValue = () => window.localStorage.getItem('mj_access') || '';

const resolveCurrentAccess = (itemId, items) => {
  if (!itemId || !items?.[itemId]) return { item: null, isValid: false };
  const item = items[itemId];
  const campaign = items?.[item?.campaign];
  const world = items?.[item?.world];
  const universe = items?.[item?.universe];
  const canMj = isMjForItem(item, items) || canUseMjForItem(item, items);
  if (canMj) {
    return { item, isValid: true };
  }
  if (!hasResolvedPermissionScope(item, campaign, world, universe)) {
    return { item, isValid: false };
  }
  return {
    item,
    isValid: canAccessItem(item, campaign, world, universe),
  };
};

const TabButton = ({ active, children, onClick }) => (
  <div
    role='button'
    tabIndex={0}
    onClick={onClick}
    onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    }}
    className='flex-1 relative cursor-pointer'
  >
    <div
      className={`${
        active ? 'bg-gradient-to-b from-main to-primary-d absolute py-07' : ''
      }`}
      style={{
        width: '120%',
        marginLeft: '-10%',
        paddingLeft: '10%',
        bottom: 0,
        zIndex: 0,
      }}
    ></div>
    <div
      className={`rounded-br-lg ${active ? 'bg-main absolute py-07' : ''}`}
      style={{
        width: '10%',
        marginLeft: '-10%',
        paddingLeft: '10%',
        bottom: 0,
        zIndex: 0,
      }}
    ></div>
    <div
      className={`rounded-bl-lg ${active ? 'bg-main absolute py-07' : ''}`}
      style={{
        width: '10%',
        marginLeft: '100%',
        paddingLeft: '10%',
        bottom: 0,
        zIndex: 0,
      }}
    ></div>
    <div
      style={{ zIndex: 1 }}
      className={`relative rounded-tl-lg rounded-tr-lg flex h-full items-center justify-center text-center py-05 px-1 cursor-pointer whitespace-nowrap font-condensed text-sm uppercase tracking-wide transition-all hover:opacity-75 ${
        active
          ? 'bg-gradient-to-b from-main to-primary-d text-contrast'
          : 'text-contrast text-opacity-60'
      }`}
    >
      {children}
    </div>
  </div>
);

const MessageLine = ({ message, tone = 'info' }) => {
  if (!message) return null;
  const toneClass =
    tone === 'error' ? 'text-secondary' : 'text-contrast text-opacity-80';
  return <div className={`text-sm ${toneClass}`}>{message}</div>;
};

const discordStatusLabels = {
  connected: 'Connexion Discord réussie.',
  linked: 'Compte Discord lié.',
  already_linked: 'Ce compte Discord est déjà lié à un autre compte.',
  email_used:
    'Un compte existe déjà avec cet email. Connecte-toi puis lie Discord depuis ton profil.',
  user_not_found: 'Compte utilisateur introuvable.',
  error: 'Connexion Discord impossible.',
};

const AuthViewLink = ({ active, children, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className={`relative bg-transparent border-0 p-0 pb-03 text-sm font-condensed uppercase tracking-wide transition ${
      active
        ? 'text-contrast'
        : 'text-contrast text-opacity-55 hover:text-opacity-85'
    }`}
  >
    <span>{children}</span>
    <span
      className={`absolute left-0 right-0 -bottom-px h-px2 transition ${
        active ? 'bg-secondary' : 'bg-transparent'
      }`}
    />
  </button>
);

const RolesPanel = ({ title, entries, ownerUsers, loading, onNavigateToItem }) => (
  <div className='rounded border border-main-d border-opacity-40 bg-main bg-opacity-35 p-1 flex flex-col gap-05 min-h-140'>
    <div className='text-xs uppercase tracking-wide font-condensed text-contrast text-opacity-60'>
      {title}
    </div>
    <div className='flex-1 overflow-y-auto pr-02 flex flex-col gap-05'>
      {loading ? (
        <div className='text-sm text-contrast text-opacity-60'>
          Chargement...
        </div>
      ) : (
        entries.map(entry => {
          const canNavigate =
            (entry.role === 'owner' || entry.role === 'admin') &&
            typeof onNavigateToItem === 'function';
          return (
            <div
              key={entry._id}
              className='text-sm text-contrast text-opacity-85 flex items-start justify-between gap-05 bg-main bg-opacity-30 rounded px-075 py-05'
            >
              <span className='truncate flex-1'>{entry.label}</span>
              <div className='flex items-center gap-05 shrink-0'>
                <span className='uppercase text-xs bg-secondary bg-opacity-20 text-secondary rounded px-03 py-02 shrink-0'>
                  {entry.role}
                </span>
                {canNavigate ? (
                  <button
                    type='button'
                    onClick={() => onNavigateToItem(entry._id)}
                    className='flex items-center justify-center transition hover:opacity-50'
                  >
                    <FaChevronRight size={12} />
                  </button>
                ) : null}
              </div>
            </div>
          );
        })
      )}
    </div>
  </div>
);

const ConnectedProfileView = ({
  authUser,
  onLogout,
  roleUniverses,
  roleCampaigns,
  ownerUsers,
  roleLoading,
  onNavigateToItem,
}) => {
  const showUniversesSection = roleLoading || roleUniverses.length > 0;
  const showCampaignsSection = roleLoading || roleCampaigns.length > 0;
  const visibleRoleSectionsCount =
    (showUniversesSection ? 1 : 0) + (showCampaignsSection ? 1 : 0);
  const roleSectionsGridClass =
    visibleRoleSectionsCount <= 1
      ? 'grid-cols-1'
      : 'grid-cols-1 md:grid-cols-2';

  return (
    <div className='flex flex-col gap-1 mt-1'>
      <div className='rounded border border-primary border-opacity-30 bg-primary bg-opacity-20 p-1'>
        <div className='text-xs uppercase tracking-widest font-condensed text-contrast text-opacity-60 mb-03'>
          Profil connecté
        </div>
        <div className='flex items-center justify-between gap-1'>
          <div className='min-w-0'>
            <div className='font-condensed text-xl text-contrast truncate'>
              {authUser.username}
            </div>
            <div className='text-xs text-contrast text-opacity-70 truncate'>
              {authUser.email}
            </div>
          </div>
          <BtnSimple
            onClick={onLogout}
            className='shrink-0'
          >
            Déconnexion
          </BtnSimple>
        </div>
      </div>

      <div className={`grid ${roleSectionsGridClass} gap-1`}>
        {showUniversesSection ? (
          <RolesPanel
            title='Univers avec rôle'
            entries={roleUniverses}
            ownerUsers={ownerUsers}
            loading={roleLoading}
            onNavigateToItem={onNavigateToItem}
          />
        ) : null}
        {showCampaignsSection ? (
          <RolesPanel
            title='Dossiers avec rôle'
            entries={roleCampaigns}
            ownerUsers={ownerUsers}
            loading={roleLoading}
            onNavigateToItem={onNavigateToItem}
          />
        ) : null}
      </div>
    </div>
  );
};

const AuthFormView = ({
  authView,
  authForm,
  forgotForm,
  authLoading,
  authMessage,
  authError,
  onAuthFieldChange,
  onForgotFieldChange,
  onLogin,
  onRegister,
  onDiscordLogin,
  onForgot,
  setAuthView,
}) => (
  <div
    className='flex flex-col gap-1 bg-primary bg-opacity-30 p-1 -mx-1 -mb-1 rounded'
    onKeyDown={event => {
      if (event.key !== 'Enter') return;
      if (authView === 'login') {
        event.preventDefault();
        onLogin();
      }
    }}
  >
    <div className='flex gap-1 flex-wrap border-b border-primary border-opacity-20 pb-05'>
      <AuthViewLink
        onClick={() => {
          if (authView === 'login') {
            onLogin();
            return;
          }
          setAuthView('login');
        }}
        active={authView === 'login'}
      >
        Connexion
      </AuthViewLink>
      <AuthViewLink
        onClick={() => setAuthView('register')}
        active={authView === 'register'}
      >
        Inscription
      </AuthViewLink>
      <AuthViewLink
        onClick={() => setAuthView('forgot')}
        active={authView === 'forgot'}
      >
        Mot de passe oublié
      </AuthViewLink>
    </div>

    {authView === 'forgot' ? (
      <>
        <InputSimple
          placeholder='email'
          forcedValue={forgotForm.email}
          onChange={e => onForgotFieldChange('email', e.target.value)}
        />
        <MessageLine message={authMessage} />
        <MessageLine
          message={authError}
          tone='error'
        />
        <div className='flex gap-1 flex-wrap'>
          <BtnSimple
            onClick={onForgot}
            disabled={authLoading}
          >
            Envoyer le lien
          </BtnSimple>
          <BtnSimple
            onClick={() => setAuthView('login')}
            disabled={authLoading}
          >
            Retour connexion
          </BtnSimple>
        </div>
      </>
    ) : (
      <>
        <BtnSimple
          className='flex items-center justify-center gap-05'
          disabled={authLoading}
          onClick={onDiscordLogin}
        >
          <FaDiscord />
          Continuer avec Discord
        </BtnSimple>
        <InputSimple
          placeholder='email'
          forcedValue={authForm.email}
          onChange={e => onAuthFieldChange('email', e.target.value)}
        />
        {authView === 'register' ? (
          <InputSimple
            placeholder='username'
            forcedValue={authForm.username}
            onChange={e => onAuthFieldChange('username', e.target.value)}
          />
        ) : null}
        <InputSimple
          placeholder='mot de passe'
          forcedValue={authForm.password}
          type='password'
          onChange={e => onAuthFieldChange('password', e.target.value)}
        />
        <MessageLine message={authMessage} />
        <MessageLine
          message={authError}
          tone='error'
        />
        <div className='flex gap-1 flex-wrap'>
          {authView === 'login' ? (
            <BtnSimple
              onClick={onLogin}
              disabled={authLoading}
            >
              Se connecter
            </BtnSimple>
          ) : (
            <BtnSimple
              onClick={onRegister}
              disabled={authLoading}
            >
              Créer le compte
            </BtnSimple>
          )}
        </div>
      </>
    )}
  </div>
);

const PasswordAccessView = ({
  accessPassword,
  passwordForm,
  passwords,
  passwordError,
  passwordMessage,
  deleteConfirmId,
  setPasswordForm,
  onApplyAccess,
  onAddPassword,
  onToggleHidden,
  onRequestDelete,
  onCancelDelete,
  onDeletePassword,
  sensors,
  onDragEnd,
}) => (
  <div className='flex flex-col gap-1 mt-1'>
    <div className='flex flex-col gap-1 bg-primary bg-opacity-50 p-1 rounded'>
      <div className='text-xs uppercase tracking-widest font-condensed text-contrast text-opacity-60'>
        Accès actif
      </div>
      <div className='flex flex-col lg:flex-row gap-1 items-stretch lg:items-center'>
        <InputSimple
          placeholder='mot de passe'
          forcedValue={accessPassword}
          type='password'
          onChange={e =>
            setPasswordForm(prev => ({
              ...prev,
              accessPassword: e.target.value,
            }))
          }
        />
        <BtnRound onClick={onApplyAccess}>
          <FaUnlock />
        </BtnRound>
      </div>
      <MessageLine message={passwordMessage} />
      <MessageLine
        message={passwordError}
        tone='error'
      />
    </div>

    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={passwords.map(password => password.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className='flex flex-col gap-05'>
          {passwords.map(password => (
            <SortableItem
              key={password.id}
              password={password}
              deleting={deleteConfirmId === password.id}
              onToggleHidden={() => onToggleHidden(password.id)}
              onRequestDelete={() => onRequestDelete(password.id)}
              onCancelDelete={onCancelDelete}
              onDelete={() => onDeletePassword(password.id)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>

    <div className='flex flex-col gap-1 bg-primary-d rounded p-1'>
      <div className='text-xs uppercase tracking-widest font-condensed text-contrast text-opacity-60'>
        Sauvegarder un mot de passe
      </div>
      <div className='flex flex-col lg:flex-row gap-1'>
        <InputSimple
          placeholder='Label du mot de passe'
          forcedValue={passwordForm.label}
          onChange={e =>
            setPasswordForm(prev => ({ ...prev, label: e.target.value }))
          }
        />
        <InputSimple
          placeholder='mot de passe'
          forcedValue={passwordForm.password}
          type='password'
          onChange={e =>
            setPasswordForm(prev => ({ ...prev, password: e.target.value }))
          }
        />
      </div>
      <div className='flex justify-end'>
        <BtnSimple onClick={onAddPassword}>Enregistrer</BtnSimple>
      </div>
    </div>
  </div>
);

export const NavigationPassword = props => {
  const { forceOpen = false, itemId } = props;
  return (
    <NavigationManagePassword
      idToOpen='passwords'
      forceOpen={forceOpen}
      itemId={itemId}
    />
  );
};

export const NavigationManagePassword = props => {
  const {
    idToOpen = 'managePassword',
    forceOpen = false,
    itemId,
    displayMode = 'modal',
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const items = useSelector(state => state.items);
  const drawerOpen = useSelector(state => state.global.drawer.open);
  const currentItemId = itemId || drawerOpen || null;

  const [activeTab, setActiveTab] = useState('auth');
  const [authUser, setAuthUser] = useState(null);
  const [authView, setAuthView] = useState('login');
  const [authForm, setAuthForm] = useState({
    email: '',
    username: '',
    password: '',
  });
  const [forgotForm, setForgotForm] = useState({ email: '' });
  const [authError, setAuthError] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [passwords, setPasswords] = useState([]);
  const [passwordsLoaded, setPasswordsLoaded] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    accessPassword: getMjAccessValue() || getLegacyPasswordValue(),
    label: '',
    password: '',
  });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const [roleUniverses, setRoleUniverses] = useState([]);
  const [roleCampaigns, setRoleCampaigns] = useState([]);
  const [ownerUsers, setOwnerUsers] = useState({});
  const [roleLoading, setRoleLoading] = useState(false);

  useEffect(() => {
    const storedPasswords = normalizePasswordEntries(
      getStorageJSON('passwords', []),
    );
    setPasswords(storedPasswords);
    setPasswordsLoaded(true);
  }, []);

  useEffect(() => {
    if (!passwordsLoaded) return;
    setStorageJSON('passwords', passwords);
  }, [passwords, passwordsLoaded]);

  useEffect(() => {
    setPasswordForm(prev => ({
      ...prev,
      accessPassword: getMjAccessValue() || getLegacyPasswordValue(),
    }));
  }, [authUser?._id]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('auth_token');
    const discordStatus = params.get('discord');
    if (tokenFromUrl) {
      window.localStorage.setItem('auth_token', tokenFromUrl);
      setAuthLoading(true);
      setAuthFeedback('Connexion Discord en cours...', '');
      params.delete('auth_token');
    }
    if (discordStatus) {
      const statusMessage =
        discordStatusLabels[discordStatus] || discordStatusLabels.error;
      if (discordStatus === 'connected' || discordStatus === 'linked') {
        setAuthFeedback(statusMessage, '');
      } else {
        setAuthFeedback('', statusMessage);
      }
      params.delete('discord');
    }
    if (tokenFromUrl || discordStatus) {
      const nextSearch = params.toString();
      const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
      window.history.replaceState(null, '', nextUrl);
    }
  }, []);

  useEffect(() => {
    const token = window.localStorage.getItem('auth_token');
    if (!token) {
      setAuthLoading(false);
      return;
    }
    let cancelled = false;

    const fetchCurrentUser = async () => {
      setAuthLoading(true);
      try {
        const data = await getMe(token);
        if (!cancelled && data?.user) {
          setAuthUser(data.user);
          await syncAuthSession(data.user);
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          window.localStorage.removeItem('auth_token');
          window.localStorage.removeItem('auth_user');
          if (!cancelled) {
            setAuthUser(null);
          }
        }
      } finally {
        if (!cancelled) {
          setAuthLoading(false);
        }
      }
    };

    fetchCurrentUser();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!authUser?._id) {
      setRoleUniverses([]);
      setRoleCampaigns([]);
      setOwnerUsers({});
      return;
    }

    let cancelled = false;
    setRoleLoading(true);

    loadUserRoles(authUser._id.toString())
      .then(data => {
        if (cancelled) return;
        setRoleUniverses(data.roleUniverses);
        setRoleCampaigns(data.roleCampaigns);
        setOwnerUsers(data.ownerUsers);
      })
      .catch(() => {
        if (cancelled) return;
        setRoleUniverses([]);
        setRoleCampaigns([]);
        setOwnerUsers({});
      })
      .finally(() => {
        if (!cancelled) {
          setRoleLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authUser?._id]);

  useEffect(() => {
    const { item, isValid } = resolveCurrentAccess(currentItemId, items);
    if (!item) return;
    dispatch(setOpenModales({ id: 'passwords', open: !isValid }));
  }, [dispatch, currentItemId, items, authUser?._id, passwords]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const setPasswordFeedback = (message = '', error = '') => {
    setPasswordMessage(message);
    setPasswordError(error);
  };

  const setAuthFeedback = (message = '', error = '') => {
    setAuthMessage(message);
    setAuthError(error);
  };

  const handleAuthChange = (key, value) => {
    setAuthForm(prev => ({ ...prev, [key]: value }));
  };

  const handleForgotChange = (key, value) => {
    setForgotForm(prev => ({ ...prev, [key]: value }));
  };

  const handleLogin = async () => {
    setAuthFeedback('', '');
    setAuthLoading(true);
    try {
      const data = await login({
        email: authForm.email.trim(),
        password: authForm.password,
      });
      if (data?.token) {
        window.localStorage.setItem('auth_token', data.token);
      }
      if (data?.user) {
        setAuthUser(data.user);
        await syncAuthSession(data.user);
      }
      setActiveTab('auth');
      setAuthView('login');
      setAuthFeedback('Connexion réussie.', '');
      setAuthForm(prev => ({ ...prev, password: '' }));
      window.location.reload();
    } catch (e) {
      setAuthFeedback('', 'connexion impossible');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async () => {
    setAuthFeedback('', '');
    setAuthLoading(true);
    try {
      await register({
        email: authForm.email.trim(),
        username: authForm.username.trim(),
        password: authForm.password,
      });
      setAuthView('login');
      setAuthFeedback('Compte créé, connecte-toi.', '');
      setAuthForm(prev => ({ ...prev, password: '' }));
    } catch (e) {
      setAuthFeedback('', 'inscription impossible');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleForgot = async () => {
    setAuthFeedback('', '');
    setAuthLoading(true);
    try {
      await requestPasswordReset({ email: forgotForm.email.trim() });
      setAuthFeedback(
        'Si cet email existe, un lien de réinitialisation a été envoyé.',
        '',
      );
    } catch (e) {
      if (e?.code === 'PASSWORD_RESET_UNSUPPORTED') {
        setAuthFeedback('', 'réinitialisation indisponible pour le moment');
      } else {
        setAuthFeedback('', 'envoi impossible');
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleDiscordLogin = async () => {
    setAuthFeedback('', '');
    setAuthLoading(true);
    try {
      const data = await getDiscordAuthUrl({
        mode: 'login',
        returnTo: `${window.location.pathname}${window.location.search || ''}`,
      });
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      setAuthFeedback('', 'connexion Discord impossible');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) {
        setAuthFeedback('', "Discord n'est pas encore configuré côté serveur.");
      } else {
        setAuthFeedback('', 'connexion Discord impossible');
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    window.localStorage.removeItem('auth_token');
    window.localStorage.removeItem('auth_user');
    setAuthUser(null);
    setAuthFeedback('Déconnecté.', '');
    window.location.reload();
  };

  const handleApplyAccess = () => {
    const nextPassword = passwordForm.accessPassword.trim();
    if (nextPassword) {
      window.localStorage.setItem('mj_access', nextPassword);
      window.localStorage.setItem('password', nextPassword);
      setPasswordFeedback('Mot de passe actif mis à jour.', '');
    } else {
      window.localStorage.removeItem('mj_access');
      window.localStorage.removeItem('password');
      setPasswordFeedback('Mot de passe actif vidé.', '');
    }
    const { item, isValid } = resolveCurrentAccess(currentItemId, items);
    if (item) {
      dispatch(setOpenModales({ id: 'passwords', open: !isValid }));
    }
    window.location.reload();
  };

  const handleAddPassword = () => {
    setPasswordFeedback('', '');
    const label = passwordForm.label.trim();
    const password = passwordForm.password.trim();
    if (!label || !password) {
      setPasswordFeedback('', 'label et mot de passe requis');
      return;
    }
    if (passwords.some(entry => entry.password === password)) {
      setPasswordFeedback('', 'ce mot de passe existe déjà');
      return;
    }
    const nextPasswords = normalizePasswordEntries([
      ...passwords,
      { label, password, hidden: false },
    ]);
    setPasswords(nextPasswords);
    setPasswordForm(prev => ({ ...prev, label: '', password: '' }));
    setPasswordFeedback('Mot de passe enregistré.', '');
  };

  const handleDragEnd = event => {
    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) {
      return;
    }
    setPasswords(prev => {
      const oldIndex = prev.findIndex(entry => entry.id === active.id);
      const newIndex = prev.findIndex(entry => entry.id === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return prev;
      }
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleToggleHidden = id => {
    setPasswords(prev =>
      prev.map(entry =>
        entry.id === id ? { ...entry, hidden: !entry.hidden } : entry,
      ),
    );
  };

  const handleDeletePassword = id => {
    setPasswords(prev => prev.filter(entry => entry.id !== id));
    setDeleteConfirmId(null);
    setPasswordFeedback('Mot de passe supprimé.', '');
  };

  const content = (
    <div className='flex flex-col gap-1'>
      <div className='flex items-end gap-1 bg-main -m-1'>
        <TabButton
          active={activeTab === 'auth'}
          onClick={() => setActiveTab('auth')}
        >
          Connexion
        </TabButton>
        <TabButton
          active={activeTab === 'passwords'}
          onClick={() => setActiveTab('passwords')}
        >
          Accès par mot de passe (legacy)
        </TabButton>
      </div>

      {activeTab === 'auth' ? (
        authUser ? (
          <ConnectedProfileView
            authUser={authUser}
            onLogout={handleLogout}
            roleUniverses={roleUniverses}
            roleCampaigns={roleCampaigns}
            ownerUsers={ownerUsers}
            roleLoading={roleLoading}
            onNavigateToItem={targetId => navigate(`/item/${targetId}`)}
          />
        ) : (
          <AuthFormView
            authView={authView}
            authForm={authForm}
            forgotForm={forgotForm}
            authLoading={authLoading}
            authMessage={authMessage}
            authError={authError}
            onAuthFieldChange={handleAuthChange}
            onForgotFieldChange={handleForgotChange}
            onLogin={handleLogin}
            onRegister={handleRegister}
            onDiscordLogin={handleDiscordLogin}
            onForgot={handleForgot}
            setAuthView={nextView => {
              setAuthView(nextView);
              setAuthFeedback('', '');
            }}
          />
        )
      ) : (
        <PasswordAccessView
          accessPassword={passwordForm.accessPassword}
          passwordForm={passwordForm}
          passwords={passwords}
          passwordError={passwordError}
          passwordMessage={passwordMessage}
          deleteConfirmId={deleteConfirmId}
          setPasswordForm={setPasswordForm}
          onApplyAccess={handleApplyAccess}
          onAddPassword={handleAddPassword}
          onToggleHidden={handleToggleHidden}
          onRequestDelete={setDeleteConfirmId}
          onCancelDelete={() => setDeleteConfirmId(null)}
          onDeletePassword={handleDeletePassword}
          sensors={sensors}
          onDragEnd={handleDragEnd}
        />
      )}
    </div>
  );

  if (displayMode === 'page') {
    return content;
  }

  return (
    <ModalGlobal
      idToOpen={idToOpen}
      title='Connexion et accès'
      forceOpen={forceOpen}
      blurBackdrop={true}
    >
      {content}
    </ModalGlobal>
  );
};

export const SortableItem = ({
  password,
  deleting,
  onToggleHidden,
  onRequestDelete,
  onCancelDelete,
  onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: password.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`rounded px-075 py-05 bg-main bg-opacity-35 ${
        password.hidden ? 'opacity-40' : ''
      }`}
    >
      <div className='flex gap-1 items-center justify-between'>
        <div className='min-w-0 flex-1'>
          <div className='text-sm uppercase font-condensed text-contrast text-opacity-50 truncate'>
            {password.label}
          </div>
          <div className='text-sm text-contrast truncate'>
            {password.password}
          </div>
        </div>
        <div className='flex items-center gap-05 shrink-0'>
          <button
            type='button'
            className='cursor-pointer hover:opacity-50 transition'
            onClick={onToggleHidden}
          >
            {password.hidden ? <FaEyeSlash /> : <FaEye />}
          </button>
          {deleting ? (
            <>
              <BtnSimple
                sm
                color='secondary'
                onClick={onDelete}
              >
                Confirmer
              </BtnSimple>
              <BtnSimple
                sm
                className='bg-main border-main-d'
                onClick={onCancelDelete}
              >
                Annuler
              </BtnSimple>
            </>
          ) : (
            <button
              type='button'
              className='text-secondary cursor-pointer hover:opacity-50 transition'
              onClick={onRequestDelete}
            >
              <FaTrashAlt />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
